import { Controller } from "@hotwired/stimulus"
import { search } from "../gate"
import { backtest, LIMIT, today } from "../backtest"
import { track } from "./analytics_controller"

const POINTS = 240
const SPARKLINE_WIDTH = 240
const SPARKLINE_HEIGHT = 60
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
const whole = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 })

function euros(value) {
  return `${value < 0 ? "-" : ""}€${whole.format(Math.abs(value))}`
}

function signedEuros(value) {
  return `${value < 0 ? "−" : "+"}${euros(Math.abs(value))}`
}

function percent(value) {
  return `${value < 0 ? "−" : "+"}${Math.abs(value).toFixed(1)}%`
}

function plainPercent(value) {
  return value == null ? "—" : `${value.toFixed(1)}%`
}

function day(date, short = false) {
  const [year, month, dayOfMonth] = date.split("-").map(Number)
  const name = MONTHS[month - 1]
  return `${dayOfMonth} ${short ? name.slice(0, 3) : name} ${year}`
}

function sentence(words) {
  if (words.length < 3) return words.join(" and ")
  return `${words.slice(0, -1).join(", ")}, and ${words[words.length - 1]}`
}

function sample(series) {
  const every = Math.ceil(series.length / POINTS)
  const sampled = series.filter((_, index) => index % every === 0)
  return sampled[sampled.length - 1] === series[series.length - 1] ? sampled : [...sampled, series[series.length - 1]]
}

function point(series, date) {
  let low = 0
  let high = series.length
  while (low < high) {
    const middle = (low + high) >> 1
    if (series[middle][0] > date) high = middle
    else low = middle + 1
  }
  return low > 0 ? series[low - 1] : null
}

function tone(value) {
  return value < 0 ? "calculator-down" : "calculator-up"
}

export default class extends Controller {
  static targets = ["form", "chips", "add", "search", "query", "suggestions", "error", "submit", "results", "slot", "resultsTemplate", "summaryTemplate", "legendTemplate", "rowTemplate", "cardTemplate"]
  static values = { search: String }

  connect() {
    document.fonts.ready.then(() => this.slotTargets.forEach((slot) => this.resize(slot)))
    this.addTarget.hidden = this.chips().length >= LIMIT
    if (this.chips().length > 0) this.calculate(false)
  }

  fit(event) {
    this.resize(event.currentTarget)
  }

  resize(slot) {
    const style = getComputedStyle(slot)
    const context = document.createElement("canvas").getContext("2d")
    context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
    const text = slot.options[slot.selectedIndex].text
    slot.style.width = `${Math.ceil(context.measureText(text).width) + parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)}px`
  }

  open() {
    this.searchTarget.hidden = false
    this.addTarget.hidden = true
    this.queryTarget.focus()
  }

  close() {
    this.searchTarget.hidden = true
    this.queryTarget.value = ""
    this.suggestionsTarget.replaceChildren()
    this.addTarget.hidden = this.chips().length >= LIMIT
  }

  keydown(event) {
    if (event.key === "Escape") {
      this.close()
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.suggestionsTarget.querySelector("button:not(:disabled)")?.click()
    }
  }

  search() {
    clearTimeout(this.timer)
    const term = this.queryTarget.value.trim()
    if (term.length < 2) {
      this.suggestionsTarget.replaceChildren()
      return
    }

    this.timer = setTimeout(async () => {
      const securities = await search(term, this.searchValue)
      if (this.queryTarget.value.trim() !== term) return

      const taken = this.symbols()
      this.suggestionsTarget.replaceChildren(...securities.map((security) => this.suggestion(security, taken.includes(security.symbol))))
    }, 250)
  }

  suggestion(security, added) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "calculator-search__option"
    button.setAttribute("role", "option")
    button.disabled = added

    const symbol = document.createElement("span")
    symbol.className = "calculator-symbol"
    symbol.textContent = security.symbol
    const name = document.createElement("span")
    name.className = "calculator-name"
    name.textContent = [security.name, security.exchange, added && "added"].filter(Boolean).join(" · ")

    button.append(symbol, name)
    button.addEventListener("click", () => this.pick(security))
    return button
  }

  pick(security) {
    const chip = document.createElement("span")
    chip.className = "calculator-chip"
    chip.append(security.symbol)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.setAttribute("aria-label", `Remove ${security.symbol}`)
    remove.innerHTML = '<svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 3l8 8M11 3l-8 8"></path></svg>'
    remove.dataset.action = "investment-calculator#remove"

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "symbols[]"
    input.value = security.symbol

    chip.append(remove, input)
    this.chipsTarget.append(chip)
    this.errorTarget.hidden = true
    this.close()
    track("backtest-symbol", { symbol: security.symbol })
  }

  remove(event) {
    event.currentTarget.closest(".calculator-chip").remove()
    this.addTarget.hidden = this.chips().length >= LIMIT
  }

  chips() {
    return Array.from(this.chipsTarget.children)
  }

  symbols() {
    return this.chips().map((chip) => chip.querySelector("input").value)
  }

  submit(event) {
    event.preventDefault()
    if (this.chips().length === 0) {
      this.errorTarget.hidden = false
      return
    }

    this.calculate(true)
  }

  async calculate(scroll) {
    const form = this.formTarget.elements
    const [dayOfMonth, month, year] = form.start.value.split("/")
    const start = `${year}-${month}-${dayOfMonth}`
    const amount = Number(form.amount.value)
    if (!(amount > 0) || !year || start > today()) return

    this.submitTarget.disabled = true
    this.resultsTarget.setAttribute("aria-busy", "true")
    const outcome = await backtest({ amount, frequency: form.frequency.value, start, symbols: this.symbols() })
    this.submitTarget.disabled = false
    this.resultsTarget.removeAttribute("aria-busy")

    this.render(outcome)

    const symbols = this.symbols()
    track("backtest-run", { symbols: symbols.join(","), count: symbols.length, frequency: form.frequency.value, years: new Date().getFullYear() - Number(year), trigger: scroll ? "submit" : "load" })
    const gaps = [...outcome.unavailable, ...outcome.missing]
    if (gaps.length > 0) track("backtest-gap", { symbols: gaps.join(",") })

    const query = new URLSearchParams(new FormData(this.formTarget))
    history.replaceState(history.state, "", `${location.pathname}?${query}`)
    if (scroll) this.resultsTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  render(outcome) {
    const fragment = this.resultsTemplateTarget.content.cloneNode(true)
    const missing = fragment.querySelector(".calculator-missing")

    const notes = []
    if (outcome.unavailable.length > 0) {
      notes.push(`Couldn't load prices for ${sentence(outcome.unavailable)} right now, so ${outcome.unavailable.length === 1 ? "it is" : "they are"} left out. Try again in a minute.`)
    }
    if (outcome.missing.length > 0) {
      notes.push(`No prices for ${sentence(outcome.missing)} on ${day(outcome.start)}, so ${outcome.missing.length === 1 ? "it is" : "they are"} left out.`)
    }
    if (notes.length > 0) {
      missing.hidden = false
      missing.textContent = notes.join(" ")
    }

    if (outcome.results.length === 0) {
      this.resultsTarget.replaceChildren(missing)
      return
    }

    this.renderSummary(fragment.querySelector(".calculator-summary"), outcome)
    this.renderChart(fragment.querySelector(".calculator-chart"), outcome)
    this.renderTable(fragment.querySelector("tbody"), outcome)
    this.resultsTarget.replaceChildren(fragment)
  }

  renderSummary(container, outcome) {
    const { results, amount, frequency, start } = outcome
    const schedule = frequency === "monthly" ? `${euros(amount)} a month in each since ${day(start)}` : `once, in each, on ${day(start)}`
    container.append(this.summary("You put in", euros(results[0].invested), document.createTextNode(schedule)))

    if (results.length === 1) {
      container.append(this.summary("Worth now", euros(results[0].worth), ...this.performance(results[0])))
      return
    }

    const best = results.reduce((top, result) => result.worth > top.worth ? result : top)
    const worst = results.reduce((bottom, result) => result.worth < bottom.worth ? result : bottom)
    container.append(this.summary("Best", euros(best.worth), ...this.performance(best)))
    container.append(this.summary("Worst", euros(worst.worth), ...this.performance(worst)))
  }

  summary(label, value, ...note) {
    const item = this.summaryTemplateTarget.content.firstElementChild.cloneNode(true)
    item.querySelector(".calculator-label").textContent = label
    item.querySelector(".calculator-summary__value").textContent = value
    item.querySelector(".calculator-note").append(...note)
    return item
  }

  performance(result) {
    const symbol = document.createElement("span")
    symbol.className = "calculator-symbol"
    symbol.textContent = result.symbol
    const change = document.createElement("span")
    change.className = tone(result.gain)
    change.textContent = percent(result.returnPercentage)
    return [symbol, " · ", change]
  }

  renderChart(element, outcome) {
    const { results, start } = outcome
    const timeline = sample([...new Set(results.flatMap((result) => result.series.map(([date]) => date)))].sort())

    element.querySelector(".calculator-chart__since").textContent = day(start)
    const legend = element.querySelector(".calculator-legend")
    results.forEach((result, index) => {
      const item = this.legendTemplateTarget.content.firstElementChild.cloneNode(true)
      item.firstElementChild.classList.add(`calculator-series--${index}`)
      item.append(result.symbol)
      legend.insertBefore(item, legend.lastElementChild)
    })

    element.setAttribute("data-backtest-chart-labels-value", JSON.stringify(timeline.map((date) => day(date, true))))
    element.setAttribute("data-backtest-chart-series-value", JSON.stringify(results.map((result) => ({
      label: result.symbol,
      data: timeline.map((date) => {
        const found = point(result.series, date)
        return found ? Math.round(found[1]) : null
      })
    }))))
    element.setAttribute("data-backtest-chart-put-in-value", JSON.stringify(timeline.map((date) => {
      const amounts = results.map((result) => point(result.series, date)).filter(Boolean).map((found) => found[2])
      return amounts.length > 0 ? Math.round(Math.max(...amounts)) : null
    })))
  }

  renderTable(body, outcome) {
    outcome.results
      .map((result, index) => [result, index])
      .sort(([first], [second]) => second.returnPercentage - first.returnPercentage)
      .forEach(([result, index]) => {
        const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
        row.querySelector(".calculator-dot").classList.add(`calculator-series--${index}`)
        row.querySelector(".calculator-symbol").textContent = result.symbol
        row.querySelector(".calculator-name").textContent = result.name
        row.querySelector(".calculator-table__invested").textContent = euros(result.invested)
        row.querySelector(".calculator-table__worth").textContent = euros(result.worth)
        row.querySelector(".calculator-table__drop").textContent = percent(result.worstDrop)
        row.querySelector(".calculator-table__annual").textContent = plainPercent(result.annual)
        const gain = row.querySelector(".calculator-table__gain")
        gain.textContent = signedEuros(result.gain)
        gain.classList.add(tone(result.gain))
        const pill = row.querySelector(".calculator-pill")
        pill.textContent = percent(result.returnPercentage)
        pill.classList.toggle("calculator-pill--down", result.gain < 0)
        row.setAttribute("data-tooltip-content-value", this.card(result, index, outcome.start))
        body.append(row)
      })
  }

  card(result, index, start) {
    const card = this.cardTemplateTarget.content.firstElementChild.cloneNode(true)
    card.querySelector(".calculator-symbol").textContent = result.symbol
    card.querySelector(".calculator-name").textContent = result.name
    const pill = card.querySelector(".calculator-pill")
    pill.textContent = percent(result.returnPercentage)
    pill.classList.toggle("calculator-pill--down", result.gain < 0)
    card.querySelector(".calculator-result__worth").textContent = euros(result.worth)
    const gain = card.querySelector(".calculator-result__gain")
    gain.textContent = signedEuros(result.gain)
    gain.classList.add(tone(result.gain))
    card.querySelector(".calculator-result__invested").textContent = euros(result.invested)

    const sparkline = card.querySelector(".calculator-sparkline")
    sparkline.classList.add(`calculator-series--${index}`)
    sparkline.setAttribute("aria-label", `${result.name} value since ${day(start)}`)
    sparkline.querySelector("polyline").setAttribute("points", this.sparkline(result))

    card.querySelector(".calculator-result__annual").textContent = plainPercent(result.annual)
    card.querySelector(".calculator-result__drop").textContent = percent(result.worstDrop)
    const best = card.querySelector(".calculator-result__best")
    best.textContent = `${result.bestYear[0]} · ${percent(result.bestYear[1])}`
    best.classList.add(tone(result.bestYear[1]))
    return card.outerHTML
  }

  sparkline(result) {
    const worths = sample(result.series).map(([, worth]) => worth)
    const low = Math.min(...worths)
    const range = (Math.max(...worths) - low) || 1
    const last = Math.max(worths.length - 1, 1)
    return worths
      .map((worth, index) => `${(index / last * SPARKLINE_WIDTH).toFixed(1)},${(SPARKLINE_HEIGHT - (worth - low) / range * SPARKLINE_HEIGHT).toFixed(1)}`)
      .join(" ")
  }
}
